import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import { useGetA2pCampaignLinkableNumbers, useLinkA2pCampaignNumbers } from '@/apis/business/setting/phoneNumbers/hook';
import { A2pCampaign, A2pLinkableNumber } from '@/apis/business/setting/phoneNumbers/types';
import { A2P_POLL_INTERVAL_MS } from './a2pConstants';
import { formatA2pNumber, formatA2pProvider, isLinkPending, isOnCampaign } from './a2pNumberFormat';

type Props = {
  open: boolean;
  campaign: A2pCampaign | null;
  onClose: () => void;
};

const BORDER_COLOR = '#E5E7EB';
const MAX_NUMBERS_PER_CAMPAIGN = 49;
const pillSx = { textTransform: 'none', fontWeight: 500, borderRadius: '999px', px: 2.5 } as const;
const outlinedPillSx = {
  ...pillSx,
  borderColor: BORDER_COLOR,
  color: 'text.primary',
  bgcolor: '#fff',
  '&:hover': { borderColor: BORDER_COLOR, bgcolor: '#F9FAFB' }
} as const;

const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

export default function LinkNumbersDrawer({ open, campaign, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useGetA2pCampaignLinkableNumbers(open ? campaign?.id : null, {
    refetchInterval: (query) => (query.state.data?.data?.some(isLinkPending) ? A2P_POLL_INTERVAL_MS : false)
  });
  const { mutateAsync: linkNumbers, isPending: submitting } = useLinkA2pCampaignNumbers();

  const campaignApproved = campaign?.status === 'approved';
  const numbers = useMemo(() => {
    const rows = data?.data || [];
    return campaignApproved ? rows : rows.filter((row) => row.link_status);
  }, [data, campaignApproved]);

  const numbersByKey = useMemo(
    () => Object.fromEntries(numbers.map((row) => [row.key, row])) as Record<string, A2pLinkableNumber>,
    [numbers]
  );

  useEffect(() => {
    setHydrated(false);
    setError(null);
    setSelectedKeys([]);
  }, [open, campaign?.id]);

  useEffect(() => {
    if (!open || hydrated || isLoading) return;
    setSelectedKeys(numbers.filter(isOnCampaign).map((row) => row.key));
    setHydrated(true);
  }, [open, numbers, isLoading, hydrated]);

  const initialKeys = useMemo(
    () =>
      numbers
        .filter(isOnCampaign)
        .map((row) => row.key)
        .sort()
        .join(','),
    [numbers]
  );
  const hasChanges = [...selectedKeys].sort().join(',') !== initialKeys;

  const toggle = (row: A2pLinkableNumber) => {
    if (isLinkPending(row)) return;
    const selected = selectedKeys.includes(row.key);
    if (!selected && !campaignApproved) return;
    if (!selected && selectedKeys.length >= MAX_NUMBERS_PER_CAMPAIGN) {
      setError(`Maximum ${MAX_NUMBERS_PER_CAMPAIGN} phone numbers can be added to a campaign.`);
      return;
    }
    setError(null);
    setSelectedKeys((prev) => (selected ? prev.filter((key) => key !== row.key) : [...prev, row.key]));
  };

  const onSubmit = async () => {
    if (!campaign || !hasChanges) return;
    setError(null);
    const selectedRows = selectedKeys.map((key) => numbersByKey[key]).filter(Boolean);
    const idsFor = (source: A2pLinkableNumber['source']) =>
      selectedRows.filter((row) => row.source === source).map((row) => row.id);
    try {
      await linkNumbers({
        id: campaign.id,
        payload: {
          host_sms_ids: idsFor('host_sms'),
          phone_number_purchase_ids: idsFor('phone_number_purchase'),
          telephony_endpoint_ids: idsFor('telephony_endpoint')
        }
      });
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to update numbers for this campaign.'));
    }
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 900,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Link Numbers
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={3}>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth>
          <InputLabel shrink id='link-phone-number-label'>
            Phone Number
          </InputLabel>
          <Select
            labelId='link-phone-number-label'
            label='Phone Number'
            multiple
            displayEmpty
            notched
            value={selectedKeys}
            disabled={isLoading}
            onChange={() => undefined}
            renderValue={(selected) =>
              (selected as string[]).length === 0 ? (
                <Typography color='text.secondary'>Select</Typography>
              ) : (
                `${(selected as string[]).length} selected`
              )
            }
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_COLOR }
            }}
            MenuProps={{
              PaperProps: { sx: { maxHeight: 360, mt: 0.5, border: `1px solid ${BORDER_COLOR}` } }
            }}
          >
            {numbers.length === 0 ? (
              <MenuItem disabled value=''>
                <Typography fontSize={13} color='text.secondary'>
                  {isLoading
                    ? 'Loading numbers…'
                    : 'No numbers available. Add a Host SMS number or buy a US number to link it to this campaign.'}
                </Typography>
              </MenuItem>
            ) : (
              numbers.map((row) => (
                <MenuItem
                  key={row.key}
                  value={row.key}
                  disabled={isLinkPending(row)}
                  onClick={() => toggle(row)}
                  sx={{ alignItems: 'flex-start', py: 1.25, whiteSpace: 'normal' }}
                >
                  <Checkbox
                    checked={selectedKeys.includes(row.key)}
                    disabled={isLinkPending(row)}
                    sx={{ py: 0, mr: 0.5 }}
                  />
                  <Box>
                    <Typography fontSize={14} color='text.primary'>
                      {formatA2pNumber(row.phone_number)}
                    </Typography>
                    <Typography fontSize={12} color='text.secondary'>
                      {formatA2pProvider(row.provider)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        <Typography fontSize={12} color='text.secondary' mt={1}>
          Select the hosted numbers to link to this campaign. Only Active numbers can be linked.
        </Typography>
      </Box>

      <Box display='flex' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop={`1px solid ${BORDER_COLOR}`}>
        <Button variant='outlined' onClick={onClose} disabled={submitting} sx={{ ...outlinedPillSx, minWidth: 80 }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => void onSubmit()}
          disabled={submitting || !hasChanges}
          sx={{ ...pillSx, minWidth: 90 }}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </Box>
    </Drawer>
  );
}
